import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

/**
 * SharkStatusLog - Institutional-grade loading component that cycles through 
 * technical "Shark Actions" to signal precision and capability
 */
const SharkStatusLog = ({ 
  isActive = false, 
  variant = "bids",  // "bids" | "dispatch" | "market" | "vault"
  className = "",
  size = "default"  // "default" | "compact"
}) => {
  const [currentActionIndex, setCurrentActionIndex] = useState(0);
  const intervalRef = useRef(null);

  // Technical action sequences based on context
  const actionSequences = {
    bids: [
      "Geocoding local zip code...",
      "Applying regional market benchmarks...",
      "Filtering for licensed-only pros...", 
      "Cross-referencing vendor availability...",
      "Validating insurance credentials...",
      "Calculating market variance thresholds...",
      "Synchronizing bid orchestration engine...",
      "Finalizing bid search parameters..."
    ],
    dispatch: [
      "Initializing vendor outreach matrix...",
      "Querying licensed contractor database...",
      "Applying geographic service boundaries...",
      "Validating bid workflow compliance...",
      "Activating bid orchestration protocols...",
      "Establishing secure communication channels...",
      "Broadcasting job specifications...",
      "Monitoring vendor response metrics..."
    ],
    market: [
      "Querying regional market indices...",
      "Processing local ZIP pricing data...",
      "Applying temporal variance algorithms...",
      "Cross-validating benchmark accuracy...",
      "Calculating confidence intervals...",
      "Updating market intelligence cache...",
      "Synchronizing pricing models...",
      "Finalizing market analysis..."
    ],
    vault: [
      "Getting your payment ready...",
      "Double-checking the job details one more time...",
      "Locking in your search so pros can respond...",
      "Almost there. Finishing the handoff...",
      "Wrapping up so you can see bids soon...",
      "Saving your spot in the queue...",
      "Confirming everything looks good...",
      "You are all set. Next step is in the app."
    ]
  };

  const actions = actionSequences[variant] || actionSequences.bids;

  useEffect(() => {
    if (!isActive) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setCurrentActionIndex(0);
      return;
    }

    // Cycle through actions every 1.2-2.8 seconds for organic feel
    intervalRef.current = setInterval(() => {
      setCurrentActionIndex((prevIndex) => (prevIndex + 1) % actions.length);
    }, 1200 + Math.random() * 1600);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, actions.length]);

  if (!isActive) {
    return null;
  }

  return (
    <StatusLogContainer className={className} $size={size}>
      <StatusIndicator $size={size}>
        <PulseRing $size={size} />
        <PulseCore $size={size} />
      </StatusIndicator>
      <StatusText $size={size}>
        {actions[currentActionIndex]}
      </StatusText>
    </StatusLogContainer>
  );
};

const StatusLogContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.$size === "compact" ? "8px" : "12px"};
  padding: ${props => props.$size === "compact" ? "8px 12px" : "12px 16px"};
  font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-weight: 500;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: ${props => props.$size === "compact" ? "8px" : "10px"};
  color: #334155;
  font-weight: 500;
  letter-spacing: -0.01em;
`;

const StatusIndicator = styled.div`
  position: relative;
  width: ${props => props.$size === "compact" ? "12px" : "16px"};
  height: ${props => props.$size === "compact" ? "12px" : "16px"};
  flex-shrink: 0;
`;

const PulseCore = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${props => props.$size === "compact" ? "4px" : "6px"};
  height: ${props => props.$size === "compact" ? "4px" : "6px"};
  background: #1a73e8;
  border-radius: 50%;
  z-index: 2;
`;

const PulseRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${props => props.$size === "compact" ? "12px" : "16px"};
  height: ${props => props.$size === "compact" ? "12px" : "16px"};
  border: 2px solid #1a73e8;
  border-radius: 50%;
  opacity: 0.3;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 0.7;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.1);
      opacity: 0.3;
    }
    100% {
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 0.7;
    }
  }
`;

const StatusText = styled.span`
  font-size: ${props => props.$size === "compact" ? "0.75rem" : "0.8125rem"};
  line-height: 1.4;
  color: #475569;
  font-weight: 500;
  letter-spacing: 0.02em;
  min-width: 0;
  flex: 1;
  
  /* Terminal-style text rendering */
  text-rendering: geometricPrecision;
  -webkit-font-feature-settings: "liga" 0;
  font-feature-settings: "liga" 0;
`;

export default SharkStatusLog;